/**
 * Shared niche catalog — single source of truth for all 7 orchestrator slugs.
 *
 * Both `app/test-ai/_lib/constants.ts` and the landing demo sample builder
 * consume from this catalog. This ensures exact slug alignment between
 * the guided landing demo and /test-ai without any translation layer.
 *
 * This file is the CANONICAL REGISTRY. It holds:
 *  - UI catalog data (slug, label, helper) for backward compatibility
 *  - Canonical niche slugs used by the orchestrator
 *  - Ingestion community mappings (platform, identifier, searchQueries, hashtags)
 *  - Niche AI context (description, extractionFocus) for LLM extraction
 *
 * Adding a new niche = adding one entry here. All other files derive from this.
 */

export interface NicheOption {
  slug: string;
  label: string;
  helper: string;
}

export interface NicheCommunityMapping {
  platform: "reddit" | "x";
  identifier: string;
  hashtags?: string[];
  /** Targeted pain-point keywords for Brave Search queries. */
  searchQueries?: string[];
}

export interface NicheContext {
  /** Human-readable niche name shown to the model */
  name: string;
  /** Focused description that steers the model toward the right pain patterns */
  description: string;
  /** Key signals to look for in posts */
  extractionFocus: string[];
}

export interface NicheDefinition {
  /** UI-facing data (backward compatible) */
  ui: NicheOption;
  /** Ingestion community mappings */
  communities: NicheCommunityMapping[];
  /** AI extraction context */
  context: NicheContext;
}

/**
 * The 7 canonical niche definitions — ALL other orchestrator files
 * derive their slug/community/context data from this array.
 *
 * Adding a new niche: add one entry here, then run:
 *   pnpm scaffold:niche <new-slug>
 * to generate the typed config stub and validation report.
 */
export const NICHE_DEFINITIONS: readonly NicheDefinition[] = [
  {
    ui: {
      slug: "ai-writing",
      label: "AI Writing",
      helper: "Content creators struggling with AI tools",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/ChatGPT",
        searchQueries: [
          "frustrated with AI writing",
          "AI writing tool limitations",
          "wish AI could write better",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/artificial",
        searchQueries: [
          "AI writing quality issues",
          "content generation complaints",
          "AI writing missing features",
        ],
      },
      {
        platform: "x",
        identifier: "ai_writing",
        hashtags: ["#AIWriting", "#ChatGPT", "#ContentCreation"],
      },
    ],
    context: {
      name: "AI Writing Tools",
      description:
        "Writers, marketers, and content creators using AI-powered writing assistants. Focus on complaints about: output quality inconsistency, lack of brand-voice control, hallucinated facts, inability to handle niche topics, cost vs value, and workflow integration gaps.",
      extractionFocus: [
        "quality inconsistency in generated output",
        "lack of brand-voice or style control",
        "hallucinated or inaccurate content",
        "poor handling of specialised or niche topics",
        "cost-to-value frustration",
        "workflow and integration gaps",
      ],
    },
  },
  {
    ui: {
      slug: "saas-analytics",
      label: "SaaS Analytics",
      helper: "Teams drowning in dashboards, starving for insight",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/SaaS",
        searchQueries: [
          "analytics dashboard frustration",
          "SaaS metrics tool complaints",
          "wish analytics was easier",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/analytics",
        searchQueries: [
          "analytics tool terrible",
          "data visualization struggles",
          "reporting tool wish list",
        ],
      },
      {
        platform: "x",
        identifier: "saas_analytics",
        hashtags: ["#SaaS", "#Analytics", "#DataDriven"],
      },
    ],
    context: {
      name: "SaaS Analytics",
      description:
        "SaaS founders and operators seeking better analytics and metrics tools. Focus on frustrations with: dashboard complexity, missing key metrics, slow reporting, data silos, inability to customise reports, and high pricing relative to value delivered.",
      extractionFocus: [
        "dashboard complexity that hides insights",
        "missing metrics and report types",
        "slow or unreliable reporting",
        "data silos preventing unified views",
        "inability to customise or white-label reports",
        "pricing frustration relative to value",
      ],
    },
  },
  {
    ui: {
      slug: "productivity-tools",
      label: "Productivity Tools",
      helper: "Knowledge workers juggling too many apps",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/productivity",
        searchQueries: [
          "productivity app frustration",
          "time management tool complaints",
          "wish there was a better way",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/getDiscipline",
        searchQueries: [
          "focus app isn't working",
          "task app wastes time",
          "productivity tools let me down",
          "to-do list burnout",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/DecidingToBeBetter",
        searchQueries: [
          "can't stay productive",
          "procrastination tools don't help",
          "habit tracker failed",
          "accountability tools missing",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/Efficiency",
        searchQueries: [
          "workflow tool frustration",
          "optimizing daily routine fails",
          "automation tool disappointments",
          "efficiency hacks don't stick",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/tipsandtricks",
        searchQueries: [
          "productivity tip burnout",
          "keyboard shortcut overwhelm",
          "shortcut app missing features",
          "life hack fatigue",
        ],
      },
      {
        platform: "x",
        identifier: "productivity_tools",
        hashtags: ["#Productivity", "#TimeManagement", "#Efficiency"],
      },
    ],
    context: {
      name: "Productivity Tools",
      description:
        "Knowledge workers seeking better productivity and time-management solutions. Focus on complaints about: tool fatigue from too many apps, notification overwhelm, lack of deep-work focus features, poor cross-tool integration, and metrics that don't actually improve output.",
      extractionFocus: [
        "app fatigue from juggling too many tools",
        "notification and context-switching overwhelm",
        "lack of deep-work or focus-mode features",
        "poor integration between productivity apps",
        "tracking that doesn't lead to real improvement",
      ],
    },
  },
  {
    ui: {
      slug: "dev-tools",
      label: "Dev Tools",
      helper: "Developer friction around speed and reliability",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/webdev",
        searchQueries: [
          "developer tool frustration",
          "wish my IDE could",
          "build tool hates",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/programming",
        searchQueries: [
          "developer tools broken",
          "programming workflow pain",
          "tool complaints debugging",
        ],
      },
      {
        platform: "x",
        identifier: "dev_tools",
        hashtags: ["#DevTools", "#Coding", "#Developer"],
      },
    ],
    context: {
      name: "Developer Tools",
      description:
        "Software developers frustrated with their tooling. Focus on complaints about: slow build times, poor DX, debugging pain, lack of observability, config complexity, documentation gaps, and tools that break workflows instead of improving them.",
      extractionFocus: [
        "slow build and deploy cycles",
        "poor developer experience and UX",
        "debugging and error-investigation pain",
        "lack of observability and monitoring",
        "configuration complexity and boilerplate",
        "documentation that doesn't match reality",
      ],
    },
  },
  {
    ui: {
      slug: "email-marketing",
      label: "Email Marketing",
      helper: "Marketers fighting deliverability and segmentation",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/EmailMarketing",
        searchQueries: [
          "email marketing platform frustration",
          "email automation tool complaints",
          "email deliverability issues",
          "cold email tool struggles",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/marketing",
        searchQueries: [
          "email marketing struggle",
          "automation tool frustration",
          "wish email platform could",
          "email campaign失望",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/startups",
        searchQueries: [
          "email marketing for startup",
          "startup email tool recommendation",
          "email growth hacking struggle",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/smallbusiness",
        searchQueries: [
          "email marketing small business",
          "newsletter tool frustration",
          "email list building struggle",
          "email platform too expensive",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/entrepreneur",
        searchQueries: [
          "email marketing automation limits",
          "email marketing ROI frustration",
          "email tool not worth it",
        ],
      },
      {
        platform: "x",
        identifier: "email_marketing",
        hashtags: ["#EmailMarketing", "#Marketing", "#Growth"],
      },
    ],
    context: {
      name: "Email Marketing",
      description:
        "Marketers and founders running email campaigns. Focus on complaints about: deliverability issues, low open and click rates, automation complexity, segmentation limitations, template builder inflexibility, and analytics that don't tie to revenue.",
      extractionFocus: [
        "deliverability and spam-folder problems",
        "low open and click-through rates",
        "automation workflow complexity",
        "segmentation and personalisation limitations",
        "template builder inflexibility",
        "analytics not connected to real revenue",
      ],
    },
  },
  {
    ui: {
      slug: "nocode-tools",
      label: "No-Code Tools",
      helper: "Builders hitting ceilings in flexibility and scale",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/nocode",
        searchQueries: [
          "hit limitations no code",
          "no code tool frustration",
          "wish I could customize",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/NoCodeSaaS",
        searchQueries: [
          "no code scaling problems",
          "stuck without code",
          "no code complaints",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/LowCode",
        searchQueries: [
          "low code limitations",
          "hit a wall with no code",
          "need real code flexibility",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/noCode",
        searchQueries: [
          "no code frustration",
          "platform lock-in",
          "can't do what I need",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/webflow",
        searchQueries: [
          "webflow limitations",
          "no code workflow frustration",
          "wish webflow could do more",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/Bubbleio",
        searchQueries: [
          "bubble scaling problems",
          "bubble workflow frustration",
          "bubble app limitations",
        ],
      },
      {
        platform: "x",
        identifier: "nocode_tools",
        hashtags: ["#NoCode", "#LowCode", "#BuildInPublic"],
      },
    ],
    context: {
      name: "No-Code / Low-Code Tools",
      description:
        "Makers and entrepreneurs building with no-code/low-code platforms. Focus on complaints about: hitting scaling limits, inability to customise beyond templates, platform lock-in, pricing cliffs on growth, data portability issues, and feature gaps that require real code.",
      extractionFocus: [
        "scaling limits that force migration",
        "template and customisation ceilings",
        "platform lock-in and export difficulty",
        "pricing cliffs as usage grows",
        "data portability and migration pain",
        "feature gaps that still require code",
      ],
    },
  },
  {
    ui: {
      slug: "idea-validation",
      label: "Idea Validation",
      helper: "Founders vetting startup ideas before committing",
    },
    communities: [
      {
        platform: "reddit",
        identifier: "r/startups",
        searchQueries: [
          "validate idea before building",
          "nobody wants my startup",
          "how to test demand",
          "wasted months building",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/Entrepreneur",
        searchQueries: [
          "validate business idea",
          "frustrated validating idea",
          "wish I knew before launching",
          "no one will pay for this",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/SaaS",
        searchQueries: [
          "validate SaaS idea",
          "failed validation",
          "landing page conversion terrible",
          "customers won't pay",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/EntrepreneurRideAlong",
        searchQueries: [
          "validating idea",
          "pre-launch struggles",
          "should I build this",
          "how do you validate",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/smallbusiness",
        searchQueries: [
          "is there demand for",
          "market research struggle",
          "customer discovery failed",
          "wish there was a way to test",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/startupideas",
        searchQueries: [
          "how to validate",
          "idea already taken",
          "afraid to build",
          "need feedback on idea",
        ],
      },
      {
        platform: "reddit",
        identifier: "r/indiehackers",
        searchQueries: [
          "validate before coding",
          "pre-sold before building",
          "fake door test",
          "Landing page no signups",
        ],
      },
    ],
    context: {
      name: "Idea Validation",
      description:
        "Founders and entrepreneurs trying to validate startup or product ideas before committing time and money. Focus on repeated frustrations with: false-positive validation signals (landing-page signups that don't convert), wish-I-knew-before-building stories, inability to gauge real willingness-to-pay, surface-level competitive analysis missing real threats, and validation cycles that kill momentum.",
      extractionFocus: [
        "complaints about wasted time or money building unvalidated ideas",
        "frustration with landing-page signups not converting to paying customers",
        "struggles with customer interview bias and false-positive feedback",
        "inability to test willingness-to-pay before building",
        "superficial competitor analysis that misses indirect substitutes",
        "validation fatigue and momentum loss during prolonged research",
      ],
    },
  },
] as const;

// ---------------------------------------------------------------------------
// Derived exports (backward compatible with existing consumers)
// ---------------------------------------------------------------------------

/** The 7 canonical UI options — matches the original NICHE_CATALOG shape. */
export const NICHE_CATALOG: readonly NicheOption[] = NICHE_DEFINITIONS.map(
  (d) => d.ui,
);

/** Slug list for iteration — useful for map/filter operations. */
export const NICHE_SLUGS = NICHE_DEFINITIONS.map((d) => d.ui.slug);

/** Canonical union of niche slugs derived from the registry. */
export type CanonicalNicheSlug = (typeof NICHE_SLUGS)[number];

/** Look up a niche definition by slug. Returns undefined if not found. */
export function getNicheDefinition(slug: string): NicheDefinition | undefined {
  return NICHE_DEFINITIONS.find((d) => d.ui.slug === slug);
}

/** Look up a niche option (UI data) by slug. Returns undefined if not found. */
export function getNicheBySlug(slug: string): NicheOption | undefined {
  return NICHE_CATALOG.find((n) => n.slug === slug);
}

/** Returns true if the slug is a known canonical orchestrator slug. */
export function isCanonicalNicheSlug(slug: string): boolean {
  return NICHE_SLUGS.includes(slug);
}

/** Returns the community mappings for a given niche slug. */
export function getCommunityMappings(slug: string): NicheCommunityMapping[] {
  return getNicheDefinition(slug)?.communities ?? [];
}

/** Returns the AI context for a given niche slug. */
export function getNicheAiContext(slug: string): NicheContext | undefined {
  return getNicheDefinition(slug)?.context;
}

// ---------------------------------------------------------------------------
// Validation guardrail — run at startup / in tests to catch drift
// ---------------------------------------------------------------------------

/**
 * Checks that every NICHE_DEFINITIONS entry is structurally complete.
 * Throws if any niche is missing required fields.
 *
 * Use this in tests or at process startup to catch drift early.
 */
export function validateNicheDefinitions():
  | { valid: true; count: number }
  | { valid: false; errors: string[] } {
  const errors: string[] = [];

  for (const def of NICHE_DEFINITIONS) {
    if (!def.ui?.slug) errors.push(`Niche missing ui.slug`);
    if (!def.ui?.label) errors.push(`Niche missing ui.label`);
    if (!def.ui?.helper)
      errors.push(`Niche '${def.ui?.slug}' missing ui.helper`);
    if (!Array.isArray(def.communities) || def.communities.length === 0) {
      errors.push(`Niche '${def.ui?.slug}' has no community mappings`);
    }
    for (const comm of def.communities ?? []) {
      if (!comm.platform)
        errors.push(`Niche '${def.ui?.slug}' community missing platform`);
      if (!comm.identifier)
        errors.push(`Niche '${def.ui?.slug}' community missing identifier`);
    }
    if (!def.context?.name)
      errors.push(`Niche '${def.ui?.slug}' missing context.name`);
    if (!def.context?.description)
      errors.push(`Niche '${def.ui?.slug}' missing context.description`);
    if (
      !Array.isArray(def.context?.extractionFocus) ||
      def.context.extractionFocus.length === 0
    ) {
      errors.push(`Niche '${def.ui?.slug}' missing context.extractionFocus`);
    }
  }

  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, count: NICHE_DEFINITIONS.length };
}
